import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { estudianteService, caseService, userService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";

export default function EstudiantesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [cursoFilter, setCursoFilter] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Role permissions
  const [canManage, setCanManage] = useState(false);
  
  // Modal & Form States
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<any>(null);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    rut: "",
    primer_nombre: "",
    segundo_nombre: "",
    apellido_paterno: "",
    apellido_materno: "",
    curso: "",
    nivel: "",
    fecha_nacimiento: "",
    profesor_jefe_id: "",
    apoderado_nombre: "",
    apoderado_telefono: "",
    apoderado_correo: "",
  });

  useEffect(() => {
    // Check role
    const stored = localStorage.getItem("convivencia_user");
    if (stored) {
      const user = JSON.parse(stored);
      setCanManage(user.rol === "admin" || user.rol === "director");
    }

    async function loadData() {
      try {
        setError("");
        const [studData, casesData, usersData] = await Promise.all([
          estudianteService.list({ include_inactive: true }),
          caseService.list(),
          userService.list().catch(() => [])
        ]);
        if (Array.isArray(studData)) setStudents(studData);
        if (Array.isArray(casesData)) setCases(casesData.map(mapDbCaseToFrontend));
        if (Array.isArray(usersData)) {
          setTeachers(usersData.filter((u: any) => u.rol === "profesor_jefe" || u.rol === "profesor"));
        }
      } catch (err: any) {
        console.error("Error loading students:", err);
        setError("Error al cargar estudiantes desde Azure: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const cursos = useMemo(() => {
    const set = new Set(students.map((s) => s.curso));
    return Array.from(set).sort();
  }, [students]);

  const filtered = useMemo(() => {
    return students.filter((s) => {
      if (search && !s.nombre.toLowerCase().includes(search.toLowerCase()) && !(s.rut || "").includes(search)) return false;
      if (cursoFilter && s.curso !== cursoFilter) return false;
      return true;
    });
  }, [students, search, cursoFilter]);

  const getCasosCount = (studentId: string) => {
    return cases.filter((c) => c.studentId === studentId && c.estado !== "cerrado").length;
  };

  const handleOpenCreate = () => {
    setEditingStudent(null);
    setFormData({
      rut: "",
      primer_nombre: "",
      segundo_nombre: "",
      apellido_paterno: "",
      apellido_materno: "",
      curso: "",
      nivel: "Básica",
      fecha_nacimiento: "",
      profesor_jefe_id: "",
      apoderado_nombre: "",
      apoderado_telefono: "",
      apoderado_correo: "",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (student: any) => {
    setEditingStudent(student);
    setFormData({
      rut: student.rut || "",
      primer_nombre: student.primer_nombre || "",
      segundo_nombre: student.segundo_nombre || "",
      apellido_paterno: student.apellido_paterno || "",
      apellido_materno: student.apellido_materno || "",
      curso: student.curso || "",
      nivel: student.nivel || "Básica",
      fecha_nacimiento: student.fecha_nacimiento ? student.fecha_nacimiento.substring(0, 10) : "",
      profesor_jefe_id: student.profesor_jefe_id || "",
      apoderado_nombre: student.apoderado_nombre || "",
      apoderado_telefono: student.apoderado_telefono || "",
      apoderado_correo: student.apoderado_correo || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.rut || !formData.primer_nombre || !formData.apellido_paterno || !formData.apellido_materno || !formData.curso || !formData.apoderado_nombre) {
      alert("Por favor completa los campos obligatorios: RUT, Primer Nombre, Apellidos, Curso y Apoderado.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const payload = {
        rut: formData.rut,
        primer_nombre: formData.primer_nombre,
        segundo_nombre: formData.segundo_nombre || null,
        apellido_paterno: formData.apellido_paterno,
        apellido_materno: formData.apellido_materno,
        curso: formData.curso,
        nivel: formData.nivel || null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        profesor_jefe_id: formData.profesor_jefe_id || null,
        apoderado_nombre: formData.apoderado_nombre,
        apoderado_telefono: formData.apoderado_telefono || null,
        apoderado_correo: formData.apoderado_correo || null,
      };

      if (editingStudent) {
        await estudianteService.update(editingStudent.id, payload);
      } else {
        await estudianteService.create(payload);
      }
      setShowModal(false);
      
      const studData = await estudianteService.list({ include_inactive: true });
      if (Array.isArray(studData)) setStudents(studData);
    } catch (err: any) {
      console.error("Error saving student:", err);
      alert("Error al guardar estudiante: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Estás seguro de que deseas desactivar a este estudiante?")) {
      return;
    }
    try {
      setLoading(true);
      await estudianteService.delete(id);
      const studData = await estudianteService.list({ include_inactive: true });
      if (Array.isArray(studData)) setStudents(studData);
    } catch (err: any) {
      console.error("Error deleting student:", err);
      alert("Error al desactivar estudiante: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (id: string) => {
    if (!window.confirm("¿Estás seguro de que deseas reactivar a este estudiante?")) {
      return;
    }
    try {
      setLoading(true);
      await estudianteService.reactivate(id);
      const studData = await estudianteService.list({ include_inactive: true });
      if (Array.isArray(studData)) setStudents(studData);
    } catch (err: any) {
      console.error("Error reactivating student:", err);
      alert("Error al reactivar estudiante: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && students.length === 0) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando estudiantes...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Estudiantes</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Expedientes digitales y seguimiento de casos por estudiante</p>
        </div>
        {canManage && (
          <Button onClick={handleOpenCreate} className="cursor-pointer bg-primary-500 text-white hover:bg-primary-600">
            <i className="ri-user-add-line mr-1.5" /> Agregar Estudiante
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <p className="text-xs text-foreground-500 uppercase tracking-wider">Total Estudiantes</p>
          <p className="text-2xl font-bold text-foreground-950 mt-1">{students.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <p className="text-xs text-foreground-500 uppercase tracking-wider">Con Casos Activos</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">
            {new Set(cases.filter((c) => c.estado !== "cerrado").map((c) => c.studentId)).size}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <p className="text-xs text-foreground-500 uppercase tracking-wider">Con Casos Judicializados</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">
            {new Set(cases.filter((c) => c.judicializado && c.estado !== "cerrado").map((c) => c.studentId)).size}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 max-w-md relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-4 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            placeholder="Buscar por nombre o RUT..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={cursoFilter}
          onChange={(e) => setCursoFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
        >
          <option value="">Todos los cursos</option>
          {cursos.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Student Table */}
      <div className="bg-white border border-background-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Estudiante</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">RUT</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Curso</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Apoderado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Casos Activos</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((student) => {
                const casosActivos = getCasosCount(student.id);
                return (
                  <tr key={student.id} className={`border-b border-background-100 hover:bg-background-50 transition-colors ${student.activo === false ? "opacity-60 bg-background-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-primary-700">{student.nombre?.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-foreground-900">{student.nombre}</span>
                        {student.activo === false && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-700 border border-red-200">
                            Inactivo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{student.rut}</td>
                    <td className="px-4 py-3 text-foreground-600">{student.curso}</td>
                    <td className="px-4 py-3 text-foreground-600 text-xs">
                      {student.apoderado_nombre ? (
                        <div>
                          <p className="font-medium text-foreground-800">{student.apoderado_nombre}</p>
                          {student.apoderado_telefono && <p className="text-foreground-500 text-2xs">{student.apoderado_telefono}</p>}
                        </div>
                      ) : "-"}
                    </td>
                    <td className="px-4 py-3">
                      {casosActivos > 0 ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          casosActivos >= 3 ? "bg-red-100 text-red-700" : casosActivos >= 2 ? "bg-yellow-100 text-yellow-700" : "bg-primary-100 text-primary-700"
                        }`}>
                          {casosActivos} activo{casosActivos !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-foreground-400">Sin casos</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => navigate(`/estudiantes/${student.id}`)}
                          className="text-xs px-2.5 py-1.5 bg-background-100 text-foreground-700 rounded-lg font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Ver Expediente
                        </button>
                        {canManage && (
                          <>
                            <button
                              onClick={() => handleOpenEdit(student)}
                              className="p-1.5 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-lg transition-colors cursor-pointer"
                              title="Editar Estudiante"
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                            {student.activo !== false ? (
                              <button
                                onClick={() => handleDelete(student.id)}
                                className="p-1.5 bg-red-50 text-red-650 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
                                title="Desactivar Estudiante"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivate(student.id)}
                                className="p-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors cursor-pointer"
                                title="Reactivar Estudiante"
                              >
                                <i className="ri-checkbox-circle-line text-sm" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-foreground-400">
            <span className="flex items-center justify-center w-12 h-12 mx-auto mb-3"><i className="ri-user-search-line text-3xl text-foreground-300" /></span>
            <p className="text-sm">No se encontraron estudiantes</p>
          </div>
        )}
      </div>

      {/* Modal CRUD Estudiante */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingStudent ? "Editar Estudiante" : "Agregar Nuevo Estudiante"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} className="cursor-pointer">Cancelar</Button>
            <Button onClick={handleSave} className="cursor-pointer bg-primary-500 text-white hover:bg-primary-600">
              {editingStudent ? "Guardar Cambios" : "Guardar Estudiante"}
            </Button>
          </>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider">Datos Personales del Estudiante</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="RUT *"
              placeholder="12.345.678-9"
              value={formData.rut}
              onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
            />
            <Input
              label="Primer Nombre *"
              placeholder="Juan"
              value={formData.primer_nombre}
              onChange={(e) => setFormData({ ...formData, primer_nombre: e.target.value })}
            />
            <Input
              label="Segundo Nombre"
              placeholder="Andrés"
              value={formData.segundo_nombre}
              onChange={(e) => setFormData({ ...formData, segundo_nombre: e.target.value })}
            />
            <Input
              label="Apellido Paterno *"
              placeholder="Pérez"
              value={formData.apellido_paterno}
              onChange={(e) => setFormData({ ...formData, apellido_paterno: e.target.value })}
            />
            <Input
              label="Apellido Materno *"
              placeholder="González"
              value={formData.apellido_materno}
              onChange={(e) => setFormData({ ...formData, apellido_materno: e.target.value })}
            />
            <Input
              label="Fecha de Nacimiento"
              type="date"
              value={formData.fecha_nacimiento}
              onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
            />
          </div>

          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider pt-2">Datos Académicos</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Curso *"
              placeholder="1° Medio A"
              value={formData.curso}
              onChange={(e) => setFormData({ ...formData, curso: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nivel</label>
              <select
                value={formData.nivel}
                onChange={(e) => setFormData({ ...formData, nivel: e.target.value })}
                className="w-full text-sm rounded-lg border border-background-200 bg-white text-foreground-900 px-3 py-2 outline-none cursor-pointer"
              >
                <option value="Parvularia">Parvularia</option>
                <option value="Básica">Básica</option>
                <option value="Media">Media</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Profesor Jefe</label>
              <select
                value={formData.profesor_jefe_id}
                onChange={(e) => setFormData({ ...formData, profesor_jefe_id: e.target.value })}
                className="w-full text-sm rounded-lg border border-background-200 bg-white text-foreground-900 px-3 py-2 outline-none cursor-pointer"
              >
                <option value="">Seleccionar...</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider pt-2">Datos del Apoderado Responsable</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Input
                label="Nombre Completo del Apoderado *"
                placeholder="María José González"
                value={formData.apoderado_nombre}
                onChange={(e) => setFormData({ ...formData, apoderado_nombre: e.target.value })}
              />
            </div>
            <Input
              label="Teléfono del Apoderado"
              placeholder="+56 9 1234 5678"
              value={formData.apoderado_telefono}
              onChange={(e) => setFormData({ ...formData, apoderado_telefono: e.target.value })}
            />
            <Input
              label="Correo del Apoderado"
              type="email"
              placeholder="apoderado@correo.com"
              value={formData.apoderado_correo}
              onChange={(e) => setFormData({ ...formData, apoderado_correo: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}